import type { CSSProperties } from "react";
import type { ImageElementProps } from "./types";
import { resolveDynamicColor, normalizePadding } from "./utils";

const DEFAULT_FONT_FAMILY = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export interface BrandLogoElementProps extends ImageElementProps {
  /** Additional CSS class name */
  className?: string;
  /** Container style overrides */
  containerStyle?: CSSProperties;
  /** Business/place name for fallback text */
  placeName?: string;
}

/**
 * BrandLogoElement - Renders a brand logo with fallback text.
 * Used in both admin template builder and user-facing workspace.
 * Handles missing logos with professional placeholder.
 */
export function BrandLogoElement({
  src,
  alt,
  fallbackContent,
  placeName,
  brandColors,
  templateDefaults,
  style,
  size,
  opacity,
  overlayColor,
  overlayOpacity,
  imageFit = "contain",
  borderRadius,
  borderWidth,
  borderColor,
  shadow,
  className,
  containerStyle,
}: BrandLogoElementProps) {
  const resolvedBackgroundColor = style?.backgroundColor
    ? resolveDynamicColor(style.backgroundColor, templateDefaults, brandColors, style.backgroundColor)
    : undefined;
  
  const resolvedBorderColor = borderColor
    ? resolveDynamicColor(borderColor, templateDefaults, brandColors, borderColor)
    : style?.borderColor
      ? resolveDynamicColor(style.borderColor, templateDefaults, brandColors, style.borderColor)
      : undefined;
  
  const resolvedOverlayColor = overlayColor
    ? resolveDynamicColor(overlayColor, templateDefaults, brandColors, overlayColor)
    : style?.overlayColor
      ? resolveDynamicColor(style.overlayColor, templateDefaults, brandColors, style.overlayColor)
      : undefined;
  
  const padding = normalizePadding(style);
  
  const containerCss: CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    backgroundColor: resolvedBackgroundColor,
    borderRadius: borderRadius ?? style?.borderRadius ? `${borderRadius ?? style?.borderRadius}px` : undefined,
    borderWidth: borderWidth ?? style?.borderWidth ? `${borderWidth ?? style?.borderWidth}px` : undefined,
    borderColor: resolvedBorderColor,
    borderStyle: (borderWidth ?? style?.borderWidth) ? "solid" : undefined,
    boxShadow: shadow ?? style?.shadow ?? undefined,
    padding,
    opacity: opacity ?? style?.opacity ?? 1,
    ...containerStyle,
  };
  
  const imageCss: CSSProperties = {
    width: "100%",
    height: "100%",
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: imageFit ?? style?.imageFit ?? "contain",
    display: "block",
  };
  
  const overlayCss: CSSProperties = resolvedOverlayColor ? {
    position: "absolute",
    inset: 0,
    backgroundColor: resolvedOverlayColor,
    opacity: overlayOpacity ?? style?.overlayOpacity ?? 0.3,
    pointerEvents: "none",
  } : {};
  
  // Fallback display for missing logo
  const fallbackDisplay = fallbackContent ?? placeName?.[0]?.toUpperCase() ?? "L";
  const isInitial = fallbackDisplay.length === 1;
  
  return (
    <div className={className} style={containerCss}>
      {src ? (
        <>
          <img src={src} alt={alt} style={imageCss} />
          {resolvedOverlayColor && <div style={overlayCss} />}
        </>
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: isInitial ? "#e5e7eb" : "transparent",
            color: isInitial ? "#4b5563" : "#9ca3af",
            fontFamily: DEFAULT_FONT_FAMILY,
            fontSize: isInitial ? (style?.fontSize ?? 20) : Math.max(style?.fontSize ?? 16, 12),
            fontWeight: isInitial ? 600 : 500,
            letterSpacing: isInitial ? "0.2em" : (style?.letterSpacing ?? 0),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textTransform: "uppercase",
            textAlign: "center",
            padding: isInitial ? 0 : "8px",
          }}
        >
          {isInitial ? fallbackDisplay : "Logo"}
        </div>
      )}
    </div>
  );
}
